import React, { useState, useMemo, useEffect } from "react";
import {
  useRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
} from "../../../api/queries/organizationQueries";
import SafeDeleteDialog from "../../../components/ui/SafeDeleteDialog";
import { getApiErrorMessage } from "../../../utils/apiError";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { RoleTreeDialog } from "./OrgTreeDialogs";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { useCanModify } from "../../../hooks/usePermissions";
import {
  NeedCompanyHint,
  useAllCompanies,
} from "../../../components/layout/WorkingCompany";

const Roles = () => {
  // View-only roles see the list without create / edit / status actions
  const canModify = useCanModify("company-setup", { company: true });
  const { data: roles = [] } = useRoles();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const [deleting, setDeleting] = useState(null); // role
  const isSaving = createRole.isPending || updateRole.isPending;

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showTree, setShowTree] = useState(false);
  const currentUser = useCurrentUser();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    description: "",
    reportsToId: null,
    isActive: true,
    assignmentType: "Both",
  });

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "title",
    direction: "asc",
  });

  const companyId = localStorage.getItem("companyId") || null;
  const allCompanies = useAllCompanies(); // super admin on "All companies": pick one to add roles

  // useEffect(() => {
  //   if (error) toast.error(error);
  // }, [error]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-down ms-2 text-primary fs-070"></i>
    ) : (
      <i className="bi bi-sort-up ms-2 text-primary fs-070"></i>
    );
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormData({
      id: "",
      title: "",
      description: "",
      reportsToId: null,
      isActive: true,
      assignmentType: "Both",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (role) => {
    setIsEditMode(true);
    setFormData({
      id: role.id,
      title: role.title,
      description: role.description || "",
      reportsToId: role.reportsToId || null,
      assignmentType: role.assignmentType,
      isActive: role.isActive ?? true,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      title: formData.title,
      description: formData.description,
      isActive: formData.isActive,
      assignmentType: formData.assignmentType || "Both",
      reportsToId: formData.reportsToId || null,
      companyId,
    };

    try {
      if (isEditMode)
        await updateRole.mutateAsync({ id: formData.id, data: payload });
      else await createRole.mutateAsync(payload);

      toast.success(`"${formData.title}" saved successfully!`);
      setShowModal(false);
    } catch (err) {
      console.error("Role Save Error:", err);
      toast.error(getApiErrorMessage(err, "Failed to save role."));
    }
  };

  const handleStatusToggle = (role) => {
    if (role.isActive) {
      setPendingRole(role);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(role);
    }
  };

  const executeStatusUpdate = async (role) => {
    const payload = {
      title: role.title,
      description: role.description || "",
      isActive: !role.isActive,
      reportsToId: role.reportsTo?.id || null,
      assignmentType: formData.assignmentType || "Both",
      companyId,
    };

    try {
      await updateRole.mutateAsync({ id: role.id, data: payload });
      toast.success(`${role.title} status updated!`);
      setShowConfirm(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not change status."));
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    let items = Array.isArray(roles) ? [...roles] : [];
    items = items.filter((item) => {
      const matchesSearch =
        (item.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "All" ||
        (item.isActive ? "Active" : "Inactive") === statusFilter;
      return matchesSearch && matchesStatus;
    });

    return items.sort((a, b) => {
      let valA, valB;
      if (sortConfig.key === "reportsTo") {
        valA = a.reportsTo?.title?.toLowerCase() || "";
        valB = b.reportsTo?.title?.toLowerCase() || "";
      } else {
        valA = (a[sortConfig.key] || "").toString().toLowerCase();
        valB = (b[sortConfig.key] || "").toString().toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [roles, searchTerm, statusFilter, sortConfig]);

  const totalItems = processedData.length;
  const currentData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">
              Organization Roles
            </h1>
            <p className="text-muted small mb-0">
              Define hierarchy and reporting structures
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary px-3 bg-white rounded-8 fs-085 fw-semibold border-soft"
              onClick={() => setShowTree(true)}
              disabled={!currentUser.companyId}
            >
              <i className="bi bi-diagram-3 me-2"></i>Role hierarchy
            </button>
            {canModify && (
              <button
                className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
                onClick={handleOpenAdd}
                disabled={allCompanies}
              >
                <i className="bi bi-plus-lg me-2"></i>Add New
              </button>
            )}
          </div>
        </div>

        <NeedCompanyHint action="add roles or see its role hierarchy" />

        {/* Filters Row */}
        <div className="row g-2 mb-4">
          <div className="col-md-9">
            <input
              type="text"
              className="form-control filter-input"
              placeholder="Search roles by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Glass Table Container */}

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header sortable">
                <tr>
                  {[
                    { label: "ROLE TITLE", key: "title" },
                    { label: "DESCRIPTION", key: null },
                    { label: "REPORTS TO", key: "reportsTo" },
                    { label: "ASSIGNMENT TYPE", key: "assignmentType" },
                    { label: "STATUS", key: null },
                    { label: "ACTIONS", key: null },
                  ].map((col, idx) => (
                    <th
                      key={idx}
                      className={`${idx === 0 ? "ps-4" : ""} py-3 border-0 ${col.label === "STATUS" ? "text-center" : ""} ${col.label === "ACTIONS" ? "text-end pe-4" : ""} ${col.key ? "cursor-pointer" : "cursor-default"}`}
                      onClick={() => col.key && requestSort(col.key)}
                    >
                      {col.label} {col.key && getSortIcon(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="border-top-0">
                {currentData.map((role) => (
                  <tr key={role.id} className="align-middle">
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark fs-085">
                        {role.title}
                      </div>
                      <div className="text-primary fw-semibold fs-065">
                        ID: {role.id}
                      </div>
                    </td>
                    <td className="text-muted small fs-080 mw-300px">
                      {role.description || "--"}
                    </td>
                    <td className="fs-080 text-slate-700">
                      {role.reportsToId ? (
                        <span className="fw-medium">
                          {role.reportsToTitle || role.reportsTo}
                        </span>
                      ) : (
                        <span className="text-muted small italic text-uppercase fs-065">
                          Top Level
                        </span>
                      )}
                    </td>
                    <td className="fs-080 text-slate-700">
                      {role.assignmentType}
                    </td>
                    <td className="text-center">
                      <span
                        className={`status-pill status-pill--${role.isActive ? "green" : "gray"}`}
                      >
                        {toCamelCase(role.isActive ? "Active" : "Inactive")}
                      </span>
                    </td>
                    <td className="pe-4 text-end">
                      {canModify ? (
                        <>
                          <div className="d-flex justify-content-end gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary px-3 fw-bold rounded-6 fs-075"
                              onClick={() => handleOpenEdit(role)}
                            >
                              Edit
                            </button>
                            <button
                              className={`btn btn-sm ${role.isActive ? "btn-outline-danger" : "btn-outline-success"} px-3 fw-bold rounded-6 fs-075`}
                              onClick={() => handleStatusToggle(role)}
                            >
                              {role.isActive ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger px-2 fw-bold rounded-6 fs-075"
                              onClick={() => setDeleting(role)}
                              title="Delete"
                              aria-label={`Delete ${role.title}`}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="fs-070 text-slate-400">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={currentPage}
            limit={itemsPerPage}
            total={totalItems}
            onPageChange={setCurrentPage}
            onLimitChange={(v) => {
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
            pageSizes={[10, 25, 50]}
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <Dialog
          title="Confirm Deactivation"
          onClose={() => setShowConfirm(false)}
          size="sm"
        >
          <div className="text-start">
            <p className="text-muted small">
              Are you sure you want to deactivate{" "}
              <strong>{pendingRole?.title}</strong>? This may affect reporting
              lines.
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger profile-btn"
                onClick={() => executeStatusUpdate(pendingRole)}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Main Modal */}
      {showModal && (
        <Dialog
          title={<>{isEditMode ? "Edit" : "Add"} Role</>}
          onClose={() => setShowModal(false)}
          size="md"
        >
          <form onSubmit={handleSave}>
            <div className="row g-3">
              <div className="col-12">
                <label className="profile-label">Role Title</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <label className="profile-label">
                  Reports To (Superior Role)
                </label>
                <select
                  className="form-select"
                  value={formData.reportsToId || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      reportsToId: e.target.value || null,
                    })
                  }
                >
                  <option value="">None (Top Level Role)</option>
                  {roles
                    .filter((r) => r.id !== formData.id)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.title}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-12">
                <label className="profile-label">Assignment Type</label>
                <select
                  className="form-select"
                  value={formData.assignmentType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      assignmentType: e.target.value,
                    })
                  }
                >
                  <option value="Internal">Internal</option>
                  <option value="Deployed">Deployed</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              <div className="col-12">
                <label className="profile-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label className="form-check-label fs-085">
                    Active Status
                  </label>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary profile-btn"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Role"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
      {showTree && (
        <RoleTreeDialog
          companyId={currentUser.companyId}
          onClose={() => setShowTree(false)}
        />
      )}
      {deleting && (
        <SafeDeleteDialog
          title="Delete role?"
          name={deleting.title}
          isActive={deleting.isActive}
          onDelete={() => deleteRole.mutateAsync(deleting.id)}
          onDeactivate={() =>
            updateRole.mutateAsync({
              id: deleting.id,
              data: { isActive: false },
            })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </Layout>
  );
};

export default Roles;
